import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  DirectionsRun,
  MenuBook,
  ReportProblem,
  Restaurant,
  Storefront,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import HousePageBackLink from '../components/HousePageBackLink';
import axios from '../api/axios';
import { ContentEditDialog, ContentViewDialog } from './content-page/ContentPageDialogs';
import {
  ContentCards,
  ContentPageEmptyState,
  ContentPageHeader,
} from './content-page/ContentPageSections';
import {
  canCreateContent,
  canEditContentItem,
  createInitialFormData,
  getContentPageConfig,
  isGuideType,
  isLocationType,
  isProblemType,
  supportsContentCategories,
} from './content-page/config';
import { getErrorMessage } from './content-page/helpers';
import { useHouseContentsItems, useKomootPreviewImages } from './content-page/hooks';
import {
  HouseContent,
  HouseContentsPageProps,
  HouseContentStatus,
  HouseContentType,
} from './content-page/types';

const CONTENT_TYPE_ROUTES: Record<HouseContentType, string> = {
  guide: 'articles',
  activities: 'activities',
  shops: 'shops',
  restaurants: 'restaurants',
  problem: 'problems',
};

const CONTENT_TYPE_TAB_KEYS: Record<HouseContentType, string> = {
  guide: 'houseDetail.tabs.guide',
  activities: 'houseDetail.tabs.activities',
  shops: 'houseDetail.tabs.shops',
  restaurants: 'houseDetail.tabs.restaurants',
  problem: 'houseDetail.tabs.problems',
};

const CONTENT_TYPE_ICONS: Record<HouseContentType, React.ReactElement> = {
  guide: <MenuBook fontSize="small" />,
  activities: <DirectionsRun fontSize="small" />,
  shops: <Storefront fontSize="small" />,
  restaurants: <Restaurant fontSize="small" />,
  problem: <ReportProblem fontSize="small" />,
};

const SELECTABLE_CONTENT_TYPES: HouseContentType[] = ['guide', 'activities', 'shops', 'restaurants'];

const normalizeCategoryLabel = (category?: string | null) => category?.trim() || '';

const getCategorySortOrder = (item: HouseContent) => (
  item.categoryOrder > 0 ? item.categoryOrder : Number.MAX_SAFE_INTEGER
);

const collectCategoryOptions = (items: HouseContent[]) => {
  const categoryMap = new Map<string, { label: string; sortOrder: number }>();

  items.forEach((item) => {
    const normalizedCategory = normalizeCategoryLabel(item.category);

    if (!normalizedCategory) {
      return;
    }

    const normalizedKey = normalizedCategory.toLocaleLowerCase();

    if (!categoryMap.has(normalizedKey)) {
      categoryMap.set(normalizedKey, {
        label: normalizedCategory,
        sortOrder: getCategorySortOrder(item),
      });
      return;
    }

    const existingCategory = categoryMap.get(normalizedKey);

    if (existingCategory && getCategorySortOrder(item) < existingCategory.sortOrder) {
      existingCategory.sortOrder = getCategorySortOrder(item);
    }
  });

  return Array.from(categoryMap.values())
    .sort((left, right) => {
      if (left.sortOrder !== right.sortOrder) {
        return left.sortOrder - right.sortOrder;
      }

      return left.label.localeCompare(right.label, undefined, { sensitivity: 'base' });
    })
    .map((category) => category.label);
};

const collectOrderedCategoryLabels = (items: HouseContent[]) => collectCategoryOptions(items);

const moveCategory = (categories: string[], category: string, direction: 'up' | 'down') => {
  const currentIndex = categories.findIndex((entry) => entry.toLocaleLowerCase() === category.toLocaleLowerCase());

  if (currentIndex === -1) {
    return categories;
  }

  const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

  if (targetIndex < 0 || targetIndex >= categories.length) {
    return categories;
  }

  const reorderedCategories = [...categories];
  const [movedCategory] = reorderedCategories.splice(currentIndex, 1);
  reorderedCategories.splice(targetIndex, 0, movedCategory);
  return reorderedCategories;
};

const HouseContentsPage: React.FC<HouseContentsPageProps> = ({
  embedded = false,
  isOwner = false,
  currentUserId,
  contentType,
}) => {
  const { id: houseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState<HouseContent | null>(null);
  const [formData, setFormData] = useState(createInitialFormData(contentType));
  const [formErrors, setFormErrors] = useState<{ latitude?: string; longitude?: string }>({});
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<HouseContent['images']>([]);
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const { pageText } = getContentPageConfig(contentType, isOwner, t);
  const { items, loading, error, setError, refresh } = useHouseContentsItems({
    houseId,
    contentType,
    failedFetchMessage: pageText.failedFetch,
  });
  const komootPreviewImages = useKomootPreviewImages(contentType, items);
  const categoryOptions = collectCategoryOptions(items);

  const handleReorderCategory = async (category: string, direction: 'up' | 'down') => {
    if (!houseId) {
      return;
    }

    const reorderedCategories = moveCategory(collectOrderedCategoryLabels(items), category, direction);

    try {
      await axios.patch('/house-contents/category-order', {
        houseId,
        type: contentType,
        orderedCategories: reorderedCategories,
      });
      await refresh();
    } catch (err: any) {
      setError(getErrorMessage(err, pageText.failedSave));
    }
  };

  const handleRenameCategory = async (oldName: string, newName: string) => {
    if (!houseId) {
      return;
    }

    try {
      await axios.patch('/house-contents/category-name', {
        houseId,
        type: contentType,
        currentName: oldName,
        newName,
      });
      await refresh();
    } catch (err: any) {
      setError(getErrorMessage(err, pageText.failedSave));
      throw err;
    }
  };

  const handleContentTypeChange = (event: SelectChangeEvent) => {
    const newType = event.target.value as HouseContentType;
    navigate(`/houses/${houseId}/${CONTENT_TYPE_ROUTES[newType]}`);
  };

  const resetForm = () => {
    setFormData(createInitialFormData(contentType));
    setFormErrors({});
    setSelectedImages([]);
    setExistingImages([]);
  };

  const handleCreate = () => {
    setSelectedItem(null);
    resetForm();
    setDialogOpen(true);
  };

  const handleView = (item: HouseContent) => {
    setSelectedItem(item);
    setViewDialogOpen(true);
  };

  const handleOpenImageViewer = (index: number) => {
    setSelectedImageIndex(index);
    setImageViewerOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setImageViewerOpen(false);
  };

  const handleEdit = (item: HouseContent) => {
    setSelectedItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      category: normalizeCategoryLabel(item.category),
      location: item.location || '',
      address: item.address || '',
      latitude: item.latitude?.toString() || '',
      longitude: item.longitude?.toString() || '',
      komootUrl: item.komootUrl || '',
      severity: item.severity || 'medium',
      status: item.status,
    });
    setSelectedImages([]);
    setExistingImages(item.images || []);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedImages((currentImages) => [...currentImages, ...Array.from(event.target.files || [])]);
  };

  const handleRemoveSelectedImage = (imageName: string) => {
    setSelectedImages((currentImages) => currentImages.filter((image) => image.name !== imageName));
  };

  const handleRemoveExistingImage = (imageKey: string) => {
    setExistingImages((currentImages) => currentImages?.filter((image) => image.key !== imageKey) || []);
  };

  const buildFormData = () => {
    const payload = new FormData();

    payload.append('title', formData.title);
    payload.append('description', formData.description);
    payload.append('type', contentType);
    payload.append('houseId', houseId || '');

    if (isGuideType(contentType)) {
      payload.append('order', String(selectedItem?.order ?? items.length));
    }

    if (supportsContentCategories(contentType)) {
      payload.append('category', formData.category.trim());
    }

    if (isLocationType(contentType)) {
      payload.append('location', formData.location);
      payload.append('address', formData.address);
      payload.append('latitude', formData.latitude);
      payload.append('longitude', formData.longitude);

      if (contentType === 'activities') {
        payload.append('komootUrl', formData.komootUrl.trim());
      }
    }

    if (isProblemType(contentType)) {
      payload.append('severity', formData.severity);
    }

    if (selectedItem?.images) {
      const removedImageKeys = selectedItem.images
        .filter((image) => !existingImages?.some((existingImage) => existingImage.key === image.key))
        .map((image) => image.key);

      removedImageKeys.forEach((key) => payload.append('removedImageKeys', key));
    }

    selectedImages.forEach((image) => payload.append('images', image));

    return payload;
  };

  const handleDelete = async (itemId: string) => {
    if (!window.confirm(pageText.deleteConfirm)) {
      return;
    }

    try {
      await axios.delete(`/house-contents/${itemId}`);
      await refresh();
    } catch (err: any) {
      setError(getErrorMessage(err, pageText.failedDelete));
    }
  };

  const handleUpdateStatus = async (itemId: string, status: HouseContentStatus, fallback: string) => {
    try {
      await axios.patch(`/house-contents/${itemId}/status`, { status });
      await refresh();
    } catch (err: any) {
      setError(getErrorMessage(err, fallback));
    }
  };

  const handleSubmit = async () => {
    if (isLocationType(contentType)) {
      const errors: { latitude?: string; longitude?: string } = {};
      const lat = formData.latitude;
      const lng = formData.longitude;

      if (lat !== '' && (isNaN(Number(lat)) || Number(lat) < -90 || Number(lat) > 90)) {
        errors.latitude = t('common.helpers.invalidLatitude');
      }
      if (lng !== '' && (isNaN(Number(lng)) || Number(lng) < -180 || Number(lng) > 180)) {
        errors.longitude = t('common.helpers.invalidLongitude');
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
    }

    try {
      if (selectedItem) {
        await axios.put(`/house-contents/${selectedItem.id}`, buildFormData());

        if (isProblemType(contentType) && isOwner && selectedItem.status !== formData.status) {
          await axios.patch(`/house-contents/${selectedItem.id}/status`, {
            status: formData.status,
          });
        }
      } else {
        await axios.post('/house-contents', buildFormData());
      }

      setDialogOpen(false);
      await refresh();
    } catch (err: any) {
      setError(getErrorMessage(err, pageText.failedSave));
    }
  };

  const canCreate = canCreateContent(contentType, isOwner);
  const canEditItem = (item: HouseContent) => canEditContentItem(contentType, item, isOwner, currentUserId);
  const canEditSelectedItem = selectedItem ? canEditItem(selectedItem) : false;

  const content = (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <HousePageBackLink />
        {!isProblemType(contentType) && (
          <Select
            value={contentType}
            onChange={handleContentTypeChange}
            size="small"
            sx={{ minWidth: 160, '& .MuiSelect-select': { py: 0.75 } }}
            renderValue={(value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {CONTENT_TYPE_ICONS[value as HouseContentType]}
                {t(CONTENT_TYPE_TAB_KEYS[value as HouseContentType])}
              </Box>
            )}
          >
            {SELECTABLE_CONTENT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {CONTENT_TYPE_ICONS[type]}
                  {t(CONTENT_TYPE_TAB_KEYS[type])}
                </Box>
              </MenuItem>
            ))}
          </Select>
        )}
      </Box>

      <ContentPageHeader
        contentType={contentType}
        pageText={pageText}
        canCreate={canCreate}
        onCreate={handleCreate}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <ContentPageEmptyState pageText={pageText} canCreate={canCreate} onCreate={handleCreate} />
      ) : (
        <ContentCards
          contentType={contentType}
          items={items}
          pageText={pageText}
          isOwner={isOwner}
          canReorderCategories={isOwner && supportsContentCategories(contentType)}
          komootPreviewImages={komootPreviewImages}
          language={language}
          t={t}
          canEditItem={canEditItem}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onReorderCategory={handleReorderCategory}
          onRenameCategory={isOwner ? handleRenameCategory : undefined}
          onUpdateStatus={handleUpdateStatus}
        />
      )}

      <ContentViewDialog
        open={viewDialogOpen}
        contentType={contentType}
        selectedItem={selectedItem}
        imageViewerOpen={imageViewerOpen}
        selectedImageIndex={selectedImageIndex}
        canEditSelectedItem={canEditSelectedItem}
        language={language}
        t={t}
        onClose={handleCloseViewDialog}
        onStartEdit={() => {
          setViewDialogOpen(false);
          if (selectedItem) {
            handleEdit(selectedItem);
          }
        }}
        onOpenImageViewer={handleOpenImageViewer}
        onCloseImageViewer={() => setImageViewerOpen(false)}
        onNavigateImage={setSelectedImageIndex}
      />

      <ContentEditDialog
        open={dialogOpen}
        contentType={contentType}
        selectedItem={selectedItem}
        formData={formData}
        pageText={pageText}
        selectedImages={selectedImages}
        existingImages={existingImages}
        isOwner={isOwner}
        formErrors={formErrors}
        t={t}
        categoryOptions={categoryOptions}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        onFormChange={setFormData}
        onImagesChange={handleImagesChange}
        onRemoveSelectedImage={handleRemoveSelectedImage}
        onRemoveExistingImage={handleRemoveExistingImage}
      />
    </Container>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout title={pageText.layoutTitle}>
      {content}
    </Layout>
  );
};

export default HouseContentsPage;